import React, { useState, useEffect } from 'react';
import { 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  FormHelperText, 
  useTheme, 
  CircularProgress,
  Chip,
  IconButton,
  Box,
  Popover
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { 
  useNotificationGroups,
  useCurrentNotificationGroups,
  useAddNotificationGroup,
  useRemoveNotificationGroup,
  NotificationGroupInfo
} from '../../hooks/useNotifications';

interface NotificationGroupCardProps {
  channel: 'email' | 'sms';
  onMessage?: (message: string) => void;
}

export const NotificationGroupCard: React.FC<NotificationGroupCardProps> = ({
  channel,
  onMessage,
}) => {
  const theme = useTheme();
  const { data: groups, isLoading: groupsLoading, error: groupsError } = useNotificationGroups();
  const { data: currentGroups, isLoading: currentGroupsLoading } = useCurrentNotificationGroups(channel);
  const addGroupMutation = useAddNotificationGroup();
  const removeGroupMutation = useRemoveNotificationGroup();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [selectedGroupToAdd, setSelectedGroupToAdd] = useState<string>('');

  // Get available groups (groups not already selected)
  const availableGroups = groups?.filter(
    group => !currentGroups?.includes(group.groupName)
  ) || [];

  // Get group info for selected groups
  const getGroupInfo = (groupName: string): NotificationGroupInfo | undefined => {
    return groups?.find(g => g.groupName === groupName);
  };

  const handleAddClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setSelectedGroupToAdd('');
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
    setSelectedGroupToAdd('');
  };

  const handleAddGroup = async () => {
    if (!selectedGroupToAdd) return;

    try {
      await addGroupMutation.mutateAsync({
        channel,
        groupName: selectedGroupToAdd
      });
      onMessage?.(`Notification group "${selectedGroupToAdd}" added successfully for ${channel} notifications`);
      handleClosePopover();
    } catch (error: any) {
      onMessage?.(`Failed to add notification group: ${error.message}`);
    }
  };

  const handleRemoveGroup = async (groupName: string) => {
    try {
      await removeGroupMutation.mutateAsync({
        channel,
        groupName
      });
      onMessage?.(`Notification group "${groupName}" removed successfully from ${channel} notifications`);
    } catch (error: any) {
      onMessage?.(`Failed to remove notification group: ${error.message}`);
    }
  };

  const open = Boolean(anchorEl);
  const popoverId = open ? `add-group-popover-${channel}` : undefined;

  if (groupsLoading || currentGroupsLoading) {
    return (
      <div style={{ marginLeft: 32, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
        <CircularProgress size={20} />
        <span style={{ color: theme.palette.text.secondary }}>Loading notification groups...</span>
      </div>
    );
  }

  if (groupsError) {
    return (
      <div style={{ marginLeft: 32, marginBottom: 16, color: theme.palette.error.main }}>
        Failed to load notification groups
      </div>
    );
  }

  return (
    <div style={{ marginLeft: 32, marginBottom: 16 }}>
      <Box sx={{ marginTop: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <span style={{ color: theme.palette.text.primary, fontSize: 14, fontWeight: 500 }}>
            {channel === 'email' ? 'Email' : 'SMS'} Notification Groups:
          </span>
          {currentGroups && currentGroups.length > 0 ? (
            currentGroups.map((groupName) => {
              const groupInfo = getGroupInfo(groupName);
              return (
                <Chip
                  key={groupName}
                  label={groupName}
                  onDelete={() => handleRemoveGroup(groupName)}
                  deleteIcon={<CloseIcon sx={{ color: theme.palette.text.primary }} />}
                  disabled={removeGroupMutation.isPending}
                  sx={{ 
                    backgroundColor: theme.palette.primary.light,
                    color: theme.palette.primary.contrastText,
                    '& .MuiChip-deleteIcon': {
                      color: theme.palette.text.primary
                    }
                  }}
                  title={groupInfo?.description}
                />
              );
            })
          ) : (
            <span style={{ color: theme.palette.text.secondary, fontSize: 13, fontStyle: 'italic' }}>
              No groups selected
            </span>
          )}
          {availableGroups.length > 0 && (
            <IconButton
              size="small"
              onClick={handleAddClick}
              disabled={addGroupMutation.isPending}
              sx={{ 
                color: theme.palette.primary.main,
                '&:hover': { backgroundColor: theme.palette.action.hover }
              }}
              title="Add notification group"
            >
              <AddIcon />
            </IconButton>
          )}
        </Box>

        {/* Show descriptions for selected groups */}
        {currentGroups && currentGroups.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, marginTop: 0.5 }}>
            {currentGroups.map((groupName) => {
              const groupInfo = getGroupInfo(groupName);
              if (groupInfo?.description) {
                return (
                  <div
                    key={groupName}
                    style={{
                      color: theme.palette.text.secondary,
                      fontSize: 12,
                      fontStyle: 'italic',
                      marginLeft: 8
                    }}
                  >
                    <strong>{groupName}:</strong> {groupInfo.description}
                  </div>
                );
              }
              return null;
            })}
          </Box>
        )}
      </Box>

      {/* Popover for adding groups */}
      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, minWidth: 300 }}>
          <FormControl fullWidth>
            <InputLabel id={`add-group-${channel}-label`}>Select Group to Add</InputLabel>
            <Select
              labelId={`add-group-${channel}-label`}
              id={`add-group-${channel}-select`}
              value={selectedGroupToAdd}
              label="Select Group to Add"
              onChange={(e) => setSelectedGroupToAdd(e.target.value as string)}
            >
              {availableGroups.map((group) => (
                <MenuItem key={group.groupName} value={group.groupName}>
                  {group.groupName}
                </MenuItem>
              ))}
            </Select>
            {selectedGroupToAdd && getGroupInfo(selectedGroupToAdd)?.description && (
              <FormHelperText>
                {getGroupInfo(selectedGroupToAdd)?.description}
              </FormHelperText>
            )}
          </FormControl>
          <Box sx={{ display: 'flex', gap: 1, marginTop: 2, justifyContent: 'flex-end' }}>
            <IconButton
              size="small"
              onClick={handleClosePopover}
              disabled={addGroupMutation.isPending}
            >
              <CloseIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={handleAddGroup}
              disabled={!selectedGroupToAdd || addGroupMutation.isPending}
              color="primary"
            >
              <AddIcon />
            </IconButton>
          </Box>
        </Box>
      </Popover>
    </div>
  );
};
